import React, { useCallback, useState } from "react";
import { Eye } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";
import useWorkoutSettings from "../../hooks/useWorkoutSettings";
import DetailSheet from "../session/generate/DetailSheet";
import { OptionList, SummaryRow } from "../profile/ProfileParts";

export const SHARE_OPTIONS = [
  {
    value: "friends",
    label: "Mates",
    description: "Mates you've accepted see each workout you finish in their feed.",
  },
  {
    value: "public",
    label: "Everyone",
    description: "Anyone on Khronos sees each workout you finish.",
  },
  {
    value: "off",
    label: "Nobody",
    description: "Nothing is posted for you. You can still post a workout by hand.",
  },
];

/**
 * Who sees your training (issue #79), as one row at the top of the feed.
 *
 * It used to be a card at the foot of the feed, which an endless feed pushed
 * below Load more. At the top it answers "who can see this" before you scroll
 * past anything you posted.
 *
 * Writes straight through: useWorkoutSettings has no merge-back, so there is
 * no reason to wait for the sheet to close.
 */
const SharingRow = () => {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  const syncToApi = useCallback(
    (apiFields) => {
      if (!isAuthenticated) return;
      api.updateSettings(apiFields).catch(console.error);
    },
    [isAuthenticated],
  );

  const [workoutSettings, updateWorkoutSettings] = useWorkoutSettings({
    onSync: syncToApi,
  });

  const share = workoutSettings.shareCompletedWorkouts ?? "friends";
  const current = SHARE_OPTIONS.find((o) => o.value === share) || SHARE_OPTIONS[0];

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <SummaryRow
          Icon={Eye}
          label="Your workouts post to"
          value={current.label}
          onClick={() => setOpen(true)}
        />
      </div>
      <DetailSheet open={open} onOpenChange={setOpen} title="Who sees my workouts">
        <OptionList
          options={SHARE_OPTIONS}
          value={share}
          onChange={(value) => updateWorkoutSettings({ shareCompletedWorkouts: value })}
        />
        <p className="text-xs leading-snug text-muted-foreground">
          Saves as you choose. A posted workout can always be removed afterwards,
          from the feed or by deleting the session in your history.
        </p>
      </DetailSheet>
    </>
  );
};

export default SharingRow;
